import * as Yup from 'yup';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { wave, waveQueries } from '@round/api';
import WaveLogo from '@/ui/WaveLogo/WaveLogo';
import WarningIcon from '@/ui/icons/WarningIcon';
import { formatHandle } from '@/utility/formatHandle';
import { bundleOfferSummary, bundleOffersBody, bundleOffersValidation } from './bundleOffers';
import { ChipMultiSelect, ChipOption } from './ChipSelect';
import { localizeLanguageOptions } from './languageOptions';
import SignupWizard, {
    AccountValue,
    SignupFormValues,
    WizardStep,
    baseAccountShape,
    baseValidationShape,
    isBundleOfferEmpty,
} from './SignupWizard';
import {
    ARTIST_FAN_PAGE_NICHE_VALUE,
    AUDIENCE_TERRITORIES_COUNT,
    AccountGenresStep,
    AccountLanguagesStep,
    AccountNichesStep,
    AccountTerritoriesStep,
    AccountsStep,
    BasicsStep,
    BundleOffersStep,
    GENRE_OTHER_VALUE,
    MAX_ACCOUNT_NICHES,
    NOTIFICATION_CHANNEL_OPTIONS,
    RatesStep,
    StayingInTouchStep,
    SummaryRow,
    TermsSection,
    WhereBasedStep,
    accountLabel,
    labelsOf,
    platformLabel,
} from './steps';

// Builds only the rules for the sections this signup actually has, so a signup
// without a section is never blocked by that section's required fields.
export const buildValidationSchema = (sections: wave.SignupSection[]) => {
    const types = new Set(sections.map((section) => section.type));
    const accountShape = {
        ...baseAccountShape,
        ...(types.has('account_territories') && {
            basedInCountry: Yup.object().nullable().required('Select a country'),
            audienceTerritories: Yup.array().length(
                AUDIENCE_TERRITORIES_COUNT,
                `Pick exactly ${AUDIENCE_TERRITORIES_COUNT} territories`
            ),
        }),
        ...(types.has('account_niches') && {
            editNiches: Yup.array()
                .min(1, 'Pick at least one niche')
                .max(MAX_ACCOUNT_NICHES, `Pick up to ${MAX_ACCOUNT_NICHES} niches`),
            fanPageArtist: Yup.string()
                .trim()
                .when('editNiches', {
                    is: (niches: string[]) => niches?.includes(ARTIST_FAN_PAGE_NICHE_VALUE),
                    then: Yup.string().trim().required('Enter the artist the page is about'),
                }),
        }),
        ...(types.has('account_languages') && {
            contentLanguages: Yup.array().min(1, 'Pick at least one language'),
        }),
        ...(types.has('rates') && {
            rate: Yup.string()
                .trim()
                .required('Enter your fee')
                .test('positive', 'Enter a valid fee', (value) => Number(value) > 0),
        }),
    };
    return Yup.object().shape({
        ...(types.has('basics') && {
            firstName: baseValidationShape.firstName,
            lastName: baseValidationShape.lastName,
            email: baseValidationShape.email,
            confirmEmail: baseValidationShape.confirmEmail,
            phoneNumber: baseValidationShape.phoneNumber,
            isOver18: baseValidationShape.isOver18,
        }),
        // The based-in location is per account when there's no creator-level location section.
        ...(types.has('location') && { locationId: baseValidationShape.locationId }),
        ...(types.has('accounts') && {
            accounts: Yup.array().of(Yup.object().shape(accountShape)).min(1, 'Add at least one social account'),
        }),
        ...(types.has('bundle_offers') && bundleOffersValidation()),
        ...(types.has('terms') && { termsAccepted: baseValidationShape.termsAccepted }),
    });
};

type SectionRenderer = {
    fields: (keyof SignupFormValues)[];
    accountFields?: (keyof AccountValue)[];
    render: (section: wave.SignupSection, sections: wave.SignupSection[]) => WizardStep['render'];
};

const optionsOf = (sections: wave.SignupSection[], type: wave.SignupSectionType): ChipOption<string>[] =>
    sections.find((section) => section.type === type)?.options ?? [];

// Maps each API-driven section `type` to the frontend widget that renders it and
// the fields validated before advancing. The section list, order, titles/blurbs
// and genre options all come from the backend; only the widgets live here.
const SECTION_RENDERERS: Record<wave.SignupSectionType, SectionRenderer> = {
    basics: {
        fields: ['firstName', 'lastName', 'email', 'confirmEmail', 'isOver18'],
        render: () => () => <BasicsStep />,
    },
    accounts: {
        // Gated per account field (not on the whole `accounts` error) so errors from
        // later per-account sections can't block this step.
        fields: [],
        accountFields: ['platform', 'handle'],
        render: () => () => <AccountsStep />,
    },
    location: {
        fields: ['locationId'],
        render:
            () =>
            ({ formik, location, setLocation }) => (
                <WhereBasedStep
                    location={location}
                    onLocationChange={(next, locationId) => {
                        setLocation(next);
                        formik.setFieldValue('locationId', locationId);
                    }}
                />
            ),
    },
    genres: {
        fields: [],
        render:
            (section) =>
            ({ formik }) => (
                <div>
                    <ChipMultiSelect
                        options={section.options ?? []}
                        values={formik.values.genres}
                        onChange={(v) => formik.setFieldValue('genres', v)}
                    />
                </div>
            ),
    },
    account_territories: {
        fields: [],
        accountFields: ['basedInCountry', 'audienceTerritories'],
        render: () => () => <AccountTerritoriesStep />,
    },
    account_niches: {
        fields: [],
        accountFields: ['editNiches', 'fanPageArtist'],
        render: (section) => () => <AccountNichesStep options={section.options ?? []} />,
    },
    account_genres: {
        fields: [],
        accountFields: [],
        render: (section) => () => <AccountGenresStep options={section.options ?? []} />,
    },
    account_languages: {
        fields: [],
        accountFields: ['contentLanguages'],
        render: (section) => () => <AccountLanguagesStep options={localizeLanguageOptions(section.options ?? [])} />,
    },
    rates: {
        fields: [],
        accountFields: ['rate'],
        render: () => () => <RatesStep />,
    },
    bundle_offers: {
        fields: ['bundleOffers'],
        render: () => () => <BundleOffersStep />,
    },
    staying_in_touch: {
        fields: [],
        render: () => () => <StayingInTouchStep />,
    },
    terms: {
        fields: ['termsAccepted'],
        render: (_section, sections) => {
            const indexOf = (type: wave.SignupSectionType) => sections.findIndex((s) => s.type === type);
            const genreOptions = optionsOf(sections, 'genres');
            const accountNicheOptions = optionsOf(sections, 'account_niches');
            const accountGenreOptions = optionsOf(sections, 'account_genres');
            const accountLanguageOptions = localizeLanguageOptions(optionsOf(sections, 'account_languages'));
            const accountSectionTypes: wave.SignupSectionType[] = [
                'account_territories',
                'account_niches',
                'account_genres',
                'account_languages',
                'rates',
            ];
            const firstAccountSectionStep = sections.findIndex((s) => accountSectionTypes.includes(s.type));
            const bundleStep = indexOf('bundle_offers');
            const accountSummaryValue = (account: AccountValue) =>
                [
                    account.basedInCountry?.country,
                    account.audienceTerritories.map((territory) => territory.country).join(', '),
                    labelsOf(accountNicheOptions, account.editNiches),
                    labelsOf(accountGenreOptions, account.genres),
                    labelsOf(accountLanguageOptions, account.contentLanguages),
                    account.rate && `${account.rate} ${account.rateCurrency}`,
                ]
                    .filter(Boolean)
                    .join(' · ');
            return ({ formik, location, setStep }) => {
                const goTo = (index: number) => {
                    if (index !== -1) {
                        setStep(index);
                    }
                };
                // The bundle step is optional, so a row for a bundle nobody filled in says nothing.
                const startedBundleOffers =
                    bundleStep === -1 ? [] : formik.values.bundleOffers.filter((offer) => !isBundleOfferEmpty(offer));
                return (
                    <>
                        <div className="rounded-wm-lg border border-wm-line bg-wm-surface px-4 py-1">
                            <SummaryRow
                                label="Name"
                                value={`${formik.values.firstName} ${formik.values.lastName}`.trim()}
                                onEdit={() => goTo(indexOf('basics'))}
                            />
                            <SummaryRow
                                label="Email"
                                value={formik.values.email}
                                onEdit={() => goTo(indexOf('basics'))}
                            />
                            <SummaryRow
                                label="Phone"
                                value={formik.values.phoneNumber}
                                onEdit={() => goTo(indexOf('basics'))}
                            />
                            <SummaryRow
                                label="Socials"
                                value={formik.values.accounts
                                    .filter((a) => a.handle.trim())
                                    .map(
                                        (a) =>
                                            `${platformLabel(a.platform)} · ${formatHandle(
                                                a.platform,
                                                a.handle.trim()
                                            )}`
                                    )
                                    .join(', ')}
                                onEdit={() => goTo(indexOf('accounts'))}
                            />
                            {indexOf('location') !== -1 && (
                                <SummaryRow
                                    label="Location"
                                    value={
                                        location.city?.label ?? location.state?.label ?? location.country?.label ?? ''
                                    }
                                    onEdit={() => goTo(indexOf('location'))}
                                />
                            )}
                            {firstAccountSectionStep !== -1 &&
                                formik.values.accounts.map((account, index) => (
                                    <SummaryRow
                                        key={index}
                                        label={accountLabel(account, index)}
                                        value={accountSummaryValue(account)}
                                        onEdit={() => goTo(firstAccountSectionStep)}
                                    />
                                ))}
                            {indexOf('rates') !== -1 && (
                                <SummaryRow
                                    label="Open to CPM campaigns"
                                    value={formik.values.openToCpm ? 'Yes' : 'No'}
                                    onEdit={() => goTo(indexOf('rates'))}
                                />
                            )}
                            {startedBundleOffers.map((offer, index) => (
                                <SummaryRow
                                    key={index}
                                    label={`Bundle ${index + 1}`}
                                    value={bundleOfferSummary(offer, formik.values.accounts)}
                                    onEdit={() => goTo(bundleStep)}
                                />
                            ))}
                            {indexOf('genres') !== -1 && (
                                <SummaryRow
                                    label="Genres"
                                    value={labelsOf(genreOptions, formik.values.genres)}
                                    onEdit={() => goTo(indexOf('genres'))}
                                />
                            )}
                            {indexOf('staying_in_touch') !== -1 && (
                                <SummaryRow
                                    label="Staying in touch"
                                    value={labelsOf(NOTIFICATION_CHANNEL_OPTIONS, formik.values.notificationChannels)}
                                    onEdit={() => goTo(indexOf('staying_in_touch'))}
                                />
                            )}
                        </div>
                        <TermsSection />
                    </>
                );
            };
        },
    },
};

// The backend may serve section types this build doesn't know yet; drop them once here
// so steps, summary indices, validation and the posted body all agree. Dropping later,
// once the steps exist, would shift indices.
export const getRenderableSections = (sections: wave.SignupSection[]) =>
    sections.filter((section) => section.type in SECTION_RENDERERS);

export const buildSteps = (sections: wave.SignupSection[]): WizardStep[] =>
    sections.map((section) => {
        const renderer = SECTION_RENDERERS[section.type];
        return {
            id: section.type,
            title: section.title,
            blurb: section.blurb,
            fields: renderer.fields,
            accountFields: renderer.accountFields,
            render: renderer.render(section, sections),
        };
    });

// Per-account fields are included only when the signup has the matching section, so
// signups without account sections keep posting bare platform + handle rows.
export const buildBody = (slug: string, sections: wave.SignupSection[]) => {
    const types = new Set(sections.map((section) => section.type));
    return (values: SignupFormValues, referralCode?: string): wave.PostCreatorApplicationBody => ({
        first_name: values.firstName.trim(),
        last_name: values.lastName.trim(),
        email: values.email.trim(),
        phone_number: values.phoneNumber.trim(),
        location_id: values.locationId,
        other_socials_notes: values.otherSocials.trim(),
        accounts: values.accounts.map((account) => ({
            platform: account.platform,
            handle: account.handle.trim(),
            ...(types.has('account_territories') && {
                based_in_country: account.basedInCountry?.value ?? null,
                audience_territories: account.audienceTerritories.map((territory) => territory.value),
            }),
            ...(types.has('account_niches') && {
                edit_niches: account.editNiches,
                fan_page_artist: account.editNiches.includes(ARTIST_FAN_PAGE_NICHE_VALUE)
                    ? account.fanPageArtist.trim()
                    : '',
            }),
            ...(types.has('account_genres') && {
                genres: account.genres,
                genres_other: account.genres.includes(GENRE_OTHER_VALUE) ? account.genresOther.trim() : '',
            }),
            ...(types.has('account_languages') && { content_languages: account.contentLanguages }),
            ...(types.has('rates') && {
                rate: account.rate.trim(),
                rate_currency: account.rateCurrency,
            }),
        })),
        referral_code: referralCode,
        is_over_18: values.isOver18,
        happy_to_travel: values.happyToTravel,
        genres: values.genres,
        ...(types.has('rates') && { open_to_cpm: values.openToCpm }),
        ...(types.has('bundle_offers') && { bundle_offers: bundleOffersBody(values) }),
        notification_channels: values.notificationChannels,
        availability: values.availability,
        signup: slug,
        terms_accepted: values.termsAccepted,
    });
};

/**
 * Pool-specific signup addressed by slug. The section list, order and data are
 * fetched from the backend; each section's widget is chosen from SECTION_RENDERERS.
 */
const SlugSignupPage = () => {
    const { slug } = useParams<{ slug: string }>();
    const { data: signup, isLoading, isError } = useQuery(waveQueries.signup.options.detail(slug ?? ''));

    if (isLoading) {
        return (
            <div className="wm-root wm-auth flex min-h-[100dvh] flex-col bg-wm-bg">
                <div className="wm-container flex-1 px-5 py-6">
                    <div className="flex flex-col gap-3">
                        <div className="wm-skel h-6 w-1/4" />
                        <div className="wm-skel mt-4 h-7 w-2/3" />
                        <div className="wm-skel h-4 w-1/2" />
                        <div className="wm-skel mt-2 h-24 w-full" />
                        <div className="wm-skel mt-4 h-11 w-full" />
                    </div>
                </div>
            </div>
        );
    }

    if (isError || !signup || !slug) {
        return (
            <div className="wm-root wm-auth flex min-h-[100dvh] flex-col items-center justify-center bg-wm-bg px-5 py-12">
                <div className="wm-container text-center">
                    <WaveLogo size="md" className="mx-auto mb-6" />
                    <div className="wm-state">
                        <div className="wm-state-icon wm-state-icon--warn" aria-hidden>
                            <WarningIcon />
                        </div>
                        <h1 className="wm-state__title">This signup link isn&apos;t working</h1>
                        <p className="wm-state__body">
                            The link may be wrong or no longer active. Double-check it, or{' '}
                            <Link to="/signup" className="text-wm-accent underline underline-offset-2">
                                use the general signup
                            </Link>
                            .
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const sections = getRenderableSections(signup.sections);

    return (
        <SignupWizard
            steps={buildSteps(sections)}
            validationSchema={buildValidationSchema(sections)}
            buildBody={buildBody(slug, sections)}
        />
    );
};

export default SlugSignupPage;
